import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, SkipForward, Volume2, VolumeX } from "lucide-react";
import { Logo } from "./Logo";
import { LogoBuild } from "./LogoBuild";
import { stopScroll, startScroll } from "../../lib/lenis";
import { playLogoSound } from "../../lib/logoSound";

const SRC = "/media/intro1.mp4";
const BLACK_MS = 1000;
const LOGO_MS = 6000;

export const IntroOverlay = ({ open, onClose }) => {
  const [phase, setPhase] = useState("video");
  const [muted, setMuted] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [progress, setProgress] = useState(0);
  const vref = useRef(null);

  useEffect(() => { if (open) { setPhase("video"); setProgress(0); setBlocked(false); } }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    stopScroll();
    return () => startScroll();
  }, [open]);
  useEffect(() => {
    const v = vref.current;
    if (!open || phase !== "video" || !v) return;
    v.muted = muted;
    v.currentTime = 0;
    v.play()?.catch(() => setBlocked(true));
  }, [open, phase]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (vref.current) vref.current.muted = muted; }, [muted]);
  useEffect(() => {
    if (!blocked) return undefined;
    const retry = () => vref.current?.play()?.then(() => setBlocked(false)).catch(() => {});
    window.addEventListener("pointerdown", retry);
    window.addEventListener("keydown", retry);
    return () => { window.removeEventListener("pointerdown", retry); window.removeEventListener("keydown", retry); };
  }, [blocked]);
  useEffect(() => {
    if (!open || phase !== "logo" || muted) return undefined;
    return playLogoSound();
  }, [open, phase, muted]);
  useEffect(() => {
    if (phase === "black") { const t = setTimeout(() => setPhase("logo"), BLACK_MS); return () => clearTimeout(t); }
    if (phase === "logo") { const t = setTimeout(onClose, LOGO_MS); return () => clearTimeout(t); }
    return undefined;
  }, [phase, onClose]);

  const resume = () => { setBlocked(false); vref.current?.play()?.catch(() => setBlocked(true)); };
  const total = phase === "video" ? progress * 0.6 : phase === "black" ? 0.65 : 1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div key="intro" data-testid="intro-overlay" className="fixed inset-0 z-[100] bg-black" exit={{ opacity: 0, scale: 1.06, filter: "blur(14px)" }} transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}>
          <AnimatePresence>
            {phase === "video" && (
              <motion.video key="v" ref={vref} data-testid="intro-video-1" src={SRC} playsInline autoPlay muted preload="auto"
                onEnded={() => setPhase("black")} onError={() => setPhase("black")}
                onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime / (e.currentTarget.duration || 1))}
                exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
                className="absolute inset-0 h-full w-full object-contain sm:object-cover" />
            )}
          </AnimatePresence>
          {phase === "logo" && <LogoBuild />}
          {phase === "video" && (
            <>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/70" />
              <div className="absolute inset-x-0 top-0 flex items-center justify-between px-5 py-5 lg:px-12">
                <Logo compact />
                <span className="font-mono text-xs tracking-[0.3em] text-slate-200">INTRODUCTION</span>
              </div>
            </>
          )}
          {blocked && phase === "video" && (
            <button data-testid="intro-play-btn" onClick={resume} className="btn-primary absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><Play className="h-4 w-4" /> Play introduction</button>
          )}
          <div className="absolute inset-x-0 bottom-0 px-5 pb-6 lg:px-12 lg:pb-10">
            <div className="mb-5 h-[3px] overflow-hidden rounded-full bg-white/15">
              <div className="h-full bg-cyan-300 shadow-[0_0_10px_#22d3ee] transition-[width] duration-700" style={{ width: `${total * 100}%` }} />
            </div>
            <div className="flex items-center justify-between gap-3">
              {phase !== "black" ? (
                <button data-testid="intro-sound-btn" onClick={() => setMuted((m) => !m)} className="btn-ghost !px-4 !py-2.5 text-sm">
                  {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}<span className="hidden sm:inline">{muted ? "Sound off" : "Sound on"}</span>
                </button>
              ) : <span />}
              <button data-testid="skip-intro-btn" onClick={onClose} className="btn-primary !px-5 !py-2.5 text-sm">Skip Introduction <SkipForward className="h-4 w-4" /></button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
